import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@os/components/ui/button';
import { Card } from '@os/components/ui/card';
import { Input } from '@os/components/ui/input';
import { 
  Save, 
  FolderOpen, 
  Sheet, 
  Download, 
  Upload,
  Plus,
  Minus,
  Calculator,
  BarChart3,
  Filter,
  SortAsc,
  SortDesc,
  Settings,
  Share2
} from 'lucide-react';
import { globalVFS } from '@os/utils/storage/VFS';
import { useToast } from '@os/hooks/use-toast';
import { saveAs } from 'file-saver';
import MapleSheetChartModal from './MapleSheetChartModal';
import ChartOverlay, { ChartOverlayItem, ChartOverlayConfig } from './ChartOverlay';
import { supabase } from '@os/integrations/supabase/client';
import { useAuth } from '@os/hooks/useAuth';
import { DraggableFileDialog } from '@os/components/DraggableFileDialog';
import { ensureDir } from '@os/utils/filesystem';
import { evaluateFormula as evaluateExcelFormula } from '@os/utils/excelFormulas';
import FormulaSuggestions from './FormulaSuggestions';
import { EXCEL_FUNCTIONS } from '@os/utils/excelFunctions';
import FormulaLibraryDialog from './FormulaLibraryDialog';
import MapleSheetStatusBar from './MapleSheetStatusBar';
import MapleSheetAiToolbar from './MapleSheetAiToolbar';
import useOsDark from '../../hooks/useOsDark';

interface Cell {
  value: string;
  formula?: string;
  style?: {
    bold?: boolean;
    italic?: boolean;
    backgroundColor?: string;
    textColor?: string;
    fontSize?: number;
  };
}

interface SpreadsheetData {
  [key: string]: Cell;
}

interface MapleSheetProps {
  filePath?: string;
  initialContent?: string;
}

export const MapleSheet: React.FC<MapleSheetProps> = ({ filePath, initialContent }) => {
  const [data, setData] = useState<SpreadsheetData>({} as SpreadsheetData);
  const [selectedCell, setSelectedCell] = useState('A1');
  const [formulaInput, setFormulaInput] = useState('');
  const [sheetName, setSheetName] = useState('Untitled Spreadsheet');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDark = useOsDark();
  const lightSheetCardStyle = isDark ? undefined : { backgroundColor: '#ffffff', borderColor: '#cbd5e1' };
  const lightSheetTableStyle = isDark ? undefined : { backgroundColor: '#ffffff' };
  const lightSheetHeaderStyle = isDark ? undefined : { backgroundColor: '#f8fafc', borderColor: '#cbd5e1', color: '#1e293b' };
  const lightSheetRowHeaderStyle = isDark ? undefined : { backgroundColor: '#f8fafc', borderColor: '#cbd5e1', color: '#334155' };
  const getLightGridCellStyle = (isSelected: boolean, inDragSelect: boolean) => {
    if (isDark) return undefined;

    return {
      backgroundColor: isSelected ? '#e0f2fe' : inDragSelect ? '#f0f9ff' : '#ffffff',
      borderColor: isSelected ? '#0ea5e9' : '#cbd5e1',
      boxShadow: inDragSelect ? 'inset 0 0 0 1px #38bdf8' : undefined,
    };
  };
  const sheetCardClassName = isDark
    ? 'maplesheet-grid-surface min-h-full relative bg-card'
    : 'maplesheet-grid-surface min-h-full relative border-slate-300 bg-white';
  const sheetHeaderCellClassName = isDark
    ? 'maplesheet-grid-header border border-border w-24 h-8 bg-muted text-xs font-medium sticky top-0 z-30'
    : 'maplesheet-grid-header border border-slate-300 w-24 h-8 bg-slate-100 text-slate-800 text-xs font-medium sticky top-0 z-30';
  const sheetCornerCellClassName = isDark
    ? 'maplesheet-grid-corner border border-border w-12 h-8 bg-muted text-xs sticky left-0 top-0 z-40'
    : 'maplesheet-grid-corner border border-slate-300 w-12 h-8 bg-slate-100 text-xs sticky left-0 top-0 z-40';
  const sheetRowHeaderClassName = isDark
    ? 'maplesheet-grid-row-header border border-border w-12 h-8 bg-muted text-center text-xs font-medium sticky left-0 z-10'
    : 'maplesheet-grid-row-header border border-slate-300 w-12 h-8 bg-slate-50 text-slate-700 text-center text-xs font-medium sticky left-0 z-10';
  const getGridCellClassName = (isSelected: boolean, inDragSelect: boolean) => {
    if (isDark) {
      return `maplesheet-grid-cell border border-border w-24 h-8 p-0 cursor-pointer hover:bg-muted/50 ${
        isSelected ? 'maplesheet-grid-cell-selected bg-primary/20 border-primary focus-within:ring-2 focus-within:ring-primary/60' : ''
      } ${inDragSelect ? 'maplesheet-grid-cell-range bg-primary/20 ring-1 ring-primary' : ''}`;
    }

    return `maplesheet-grid-cell border border-slate-300 bg-white w-24 h-8 p-0 cursor-pointer hover:bg-slate-50 ${
      isSelected ? 'maplesheet-grid-cell-selected bg-sky-100 border-sky-500 focus-within:ring-2 focus-within:ring-sky-400/60' : ''
    } ${inDragSelect ? 'maplesheet-grid-cell-range bg-sky-50 ring-1 ring-sky-400' : ''}`;
  };
  // Virtualization state for large row counts
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  
  // Attach scroll and resize handlers to compute viewport height and scrollTop
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    const onResize = () => setViewportHeight(el.clientHeight);
    // Initialize on mount
    onResize();
    el.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Compute visible window and spacer heights
  const visibleStartRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT));
  const visibleRowCount = Math.ceil((viewportHeight || 0) / ROW_HEIGHT) + VIRTUAL_BUFFER;
  const visibleEndRow = Math.min(TOTAL_ROWS, visibleStartRow + visibleRowCount);
  const topPad = visibleStartRow * ROW_HEIGHT;
  const bottomPad = (TOTAL_ROWS - visibleEndRow) * ROW_HEIGHT;
  
  const { toast } = useToast();
  const [chartsOpen, setChartsOpen] = useState(false);
  const [chartTable, setChartTable] = useState<{ headers: string[]; rows: Record<string, string | number>[] } | null>(null);
  const [chartOverlays, setChartOverlays] = useState<ChartOverlayItem[]>([]);
  const [currentFilePath, setCurrentFilePath] = useState<string>('');
  const [autoSaving, setAutoSaving] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [showFileBrowser, setShowFileBrowser] = useState<boolean>(false);
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [saveFileName, setSaveFileName] = useState<string>('');
  const { user } = useAuth();
  const [osUsername, setOsUsername] = useState<string>('User');
  const [usernameReady, setUsernameReady] = useState<boolean>(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<typeof EXCEL_FUNCTIONS>([]);
  const [highlightIndex, setHighlightIndex] = useState<number>(0);
  const formulaInputRef = useRef<HTMLInputElement>(null);
  const cellEditorRef = useRef<HTMLInputElement>(null);
  const [activeEditor, setActiveEditor] = useState<'formulaBar' | 'cell' | null>(null);
  // ADD: formulas dialog open state
  const [formulasOpen, setFormulasOpen] = useState<boolean>(false);

  // ADD: compute non-empty cells for status
  const nonEmptyCells = React.useMemo(() => {
    let count = 0;
    for (const key in data) {
      const cell = data[key];
      if (!cell) continue;
      const raw = (cell.formula ?? cell.value ?? '').trim();
      if (raw.length > 0) count++;
    }
    return count;
  }, [data]);

  // Range selection state for formula editing
  const [isSelectingRange, setIsSelectingRange] = useState<boolean>(false);
  const [selectionStart, setSelectionStart] = useState<{ row: number; col: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ row: number; col: number } | null>(null);
  const [caretIndex, setCaretIndex] = useState<number>(0);
  const [isShiftDown, setIsShiftDown] = useState<boolean>(false);

  // ADD: keyboard range selection helpers (near other selection helpers)
  const startKeyboardRangeSelection = () => {
    if (!isFormulaMode()) return;
    // Capture current caret from the active editor so we insert range at the right spot
    updateCaretFromInput();

    // Initialize anchor at the currently selected cell if not set
    if (!selectionStart || !selectionEnd) {
      const { row, col } = cellIdToPosition(selectedCell);
      setSelectionStart({ row, col });
      setSelectionEnd({ row, col });
    }
    setIsSelectingRange(true);
  };

  const moveSelectionEndByArrow = (key: 'ArrowDown' | 'ArrowUp' | 'ArrowLeft' | 'ArrowRight') => {
    setSelectionEnd((prev) => {
      const anchor = selectionStart || cellIdToPosition(selectedCell);
      const current = prev || anchor;
      let row = current.row;
      let col = current.col;

      if (key === 'ArrowDown') row = Math.min(TOTAL_ROWS - 1, row + 1);
      if (key === 'ArrowUp') row = Math.max(0, row - 1);
      if (key === 'ArrowRight') col = Math.min(COLS - 1, col + 1);
      if (key === 'ArrowLeft') col = Math.max(0, col - 1);

      return { row, col };
    });
  };

  const getCellValue = (cellId: string): string => {
    const cell = data[cellId];
    if (!cell) return '';
    if (cell.formula) {
      const resolver = (id: string) => {
        const c = data[id];
        if (!c) return '';
        if (c.formula) {
          return evaluateExcelFormula(c.formula, resolver);
        }
        return c.value || '';
      };
      return evaluateExcelFormula(cell.formula, resolver);
    }
    return cell.value || '';
  };

  const handleCellChange = (cellId: string, value: string) => {
    setData(prev => ({
      ...prev,
      [cellId]: {
        ...prev[cellId],
        value,
        formula: value.startsWith('=') ? value : undefined,
      },
    }));
  };

  // Track Shift key state for range extension
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftDown(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftDown(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  // Add a global arrow navigation listener when not actively editing
  useEffect(() => {
    const onKeyDownNav = (e: KeyboardEvent) => {
      if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      // Only handle global movement when not editing any input
      if (activeEditor !== null) return;
      e.preventDefault();

      const { row, col } = cellIdToPosition(selectedCell);
      let nr = row;
      let nc = col;

      if (e.key === 'ArrowDown') nr = Math.min(TOTAL_ROWS - 1, row + 1);
      if (e.key === 'ArrowUp') nr = Math.max(0, row - 1);
      if (e.key === 'ArrowRight') nc = Math.min(COLS - 1, col + 1);
      if (e.key === 'ArrowLeft') nc = Math.max(0, col - 1);

      const nextId = getCellId(nr, nc);
      setSelectedCell(nextId);
      const nextCell = data[nextId];
      const raw = nextCell?.formula || nextCell?.value || '';
      setFormulaInput(raw);
      setActiveEditor('cell');
      focusSelectedCellEditor();
    };

    window.addEventListener('keydown', onKeyDownNav);
    return () => window.removeEventListener('keydown', onKeyDownNav);
  }, [selectedCell, data, activeEditor]);

  // Helper: are we editing a formula?
  const isFormulaMode = () => String(formulaInput ?? "").startsWith("=");

  // Insert text at current caret in whichever editor is active
  const insertTextAtCaret = (text: string) => {
    const start = caretIndex;
    const end = caretIndex;
    const before = formulaInput.slice(0, start);
    const after = formulaInput.slice(end);
    const next = before + text + after;
    setFormulaInput(next);
    // Place caret after inserted text into whichever editor is active
    setTimeout(() => {
      const el = activeEditor === 'formulaBar'
        ? formulaInputRef.current
        : activeEditor === 'cell'
          ? cellEditorRef.current
          : formulaInputRef.current;
      el?.focus();
      const pos = start + text.length;
      el?.setSelectionRange(pos, pos);
    }, 0);
    updateSuggestions(next);
  };

  const beginRangeSelection = (row: number, col: number) => {
    if (!isFormulaMode()) return;
    // Capture current caret position from whichever editor is active
    const el = activeEditor === 'formulaBar'
      ? formulaInputRef.current
      : activeEditor === 'cell'
        ? cellEditorRef.current
        : formulaInputRef.current;
    const idx = el?.selectionStart ?? formulaInput.length;
    setCaretIndex(idx);
    if (selectionStart && isShiftDown) {
      // Extend existing selection with Shift
      setSelectionEnd({ row, col });
      setIsSelectingRange(true);
    } else {
      setSelectionStart({ row, col });
      setSelectionEnd({ row, col });
      setIsSelectingRange(true);
    }
  };

  const updateRangeSelection = (row: number, col: number) => {
    if (!isSelectingRange) return;
    setSelectionEnd({ row, col });
  };

  const finalizeRangeSelection = () => {
    if (!isSelectingRange || !selectionStart || !selectionEnd) return;
    const minRow = Math.min(selectionStart.row, selectionEnd.row);
    const maxRow = Math.max(selectionStart.row, selectionEnd.row);
    const minCol = Math.min(selectionStart.col, selectionEnd.col);
    const maxCol = Math.max(selectionStart.col, selectionEnd.col);
    const startId = getCellId(minRow, minCol);
    const endId = getCellId(maxRow, maxCol);
    const rangeStr = startId === endId ? startId : `${startId}:${endId}`;
    insertTextAtCaret(rangeStr);
    setIsSelectingRange(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  // ADD: finalize range when Shift is released (so keyboard selection inserts A1:A5 automatically)
  useEffect(() => {
    if (!isShiftDown && isSelectingRange) {
      finalizeRangeSelection();
    }
  }, [isShiftDown, isSelectingRange]);

  const isCellInSelection = (row: number, col: number): boolean => {
    if (!isSelectingRange || !selectionStart || !selectionEnd) return false;
    const minRow = Math.min(selectionStart.row, selectionEnd.row);
    const maxRow = Math.max(selectionStart.row, selectionEnd.row);
    const minCol = Math.min(selectionStart.col, selectionEnd.col);
    const maxCol = Math.max(selectionStart.col, selectionEnd.col);
    return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
  };

  // Update caret index whenever selection changes in the active editor
  const updateCaretFromInput = () => {
    const el = activeEditor === 'formulaBar' ? formulaInputRef.current : cellEditorRef.current;
    const idx = el?.selectionStart ?? 0;
    setCaretIndex(idx);
  };

  // When selecting a cell, mirror its raw formula/value into the formula bar state
  const handleCellSelect = (cellId: string) => {
    setSelectedCell(cellId);
    const cell = data[cellId];
    const raw = cell?.formula || cell?.value || '';
    setFormulaInput(raw);
    updateSuggestions(raw);
  };

  // ADD: Insert a 2D table (rows x columns) starting at a given cell
  const applyTableAt = (startCell: string, table: (string | number)[][]) => {
    const startPos = cellIdToPosition(startCell);
    setData((prev) => {
      const next = { ...prev };
      for (let r = 0; r < table.length; r++) {
        for (let c = 0; c < (table[r]?.length || 0); c++) {
          const id = getCellId(startPos.row + r, startPos.col + c);
          const raw = table[r][c];
          const val = typeof raw === 'string' ? raw : String(raw ?? '');
          next[id] = {
            ...(next[id] || {}),
            value: val,
            formula: val.startsWith('=') ? val : undefined,
          };
        }
      }
      return next;
    });
    setIsSaved(false);
  };

  // ADD helpers inside MapleSheet component (near other helpers)
  const cellIdToPosition = (id: string): { row: number; col: number } => {
    const m = id.match(/^([A-Za-z]+)(\d+)$/);
    if (!m) return { row: 0, col: 0 };
    const colLetter = m[1].toUpperCase();
    const col = COLUMN_LETTERS.indexOf(colLetter);
    const row = Math.max(0, parseInt(m[2], 10) - 1);
    return { row, col };
  };

  const focusSelectedCellEditor = () => {
    setTimeout(() => {
      const el = cellEditorRef.current;
      if (el) {
        el.focus();
        const pos = String(el.value ?? '').length;
        el.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const navigateAfterSubmit = (dir: 'down' | 'up' | 'right' | 'left') => {
    const { row, col } = cellIdToPosition(selectedCell);
    let nr = row, nc = col;
    if (dir === 'down') nr = Math.min(TOTAL_ROWS - 1, row + 1);
    if (dir === 'up') nr = Math.max(0, row - 1);
    if (dir === 'right') nc = Math.min(COLS - 1, col + 1);
    if (dir === 'left') nc = Math.max(0, col - 1);
    const nextId = getCellId(nr, nc);
    setSelectedCell(nextId);
    const nextCell = data[nextId];
    const raw = nextCell?.formula || nextCell?.value || '';
    setFormulaInput(raw);
    setActiveEditor('cell');
    focusSelectedCellEditor();
  };

  const isFormulaLike = (s: string) => {
    const trimmed = String(s ?? "").trim();
    if (trimmed.length === 0) return false;
    const hasOperator = /[+\-*/^]/.test(trimmed);
    const looksArithmetic = /^[0-9\.\s\(\)]+(?:[+\-*/^][0-9\.\s\(\)]+)+$/.test(trimmed);
    const looksFunction = /^[A-Za-z]+\s*\(.+\)$/.test(trimmed); // e.g., SUM(A1:A3)
    const looksRefOrRange = /^[A-Za-z]+\d+(?::[A-Za-z]+\d+)?$/.test(trimmed); // A1 or A1:B10
    const hasRefsWithin = /[A-Za-z]+\d+/.test(trimmed); // any cell refs mixed in
    return looksArithmetic || looksFunction || looksRefOrRange || (hasRefsWithin && hasOperator);
  };

  const hasBalancedParens = (s: string) => {
    let depth = 0;
    let inQuotes = false;
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (inQuotes) continue;
      if (ch === '(') depth++;
      else if (ch === ')') depth--;
      if (depth < 0) return false;
    }
    return depth === 0;
  };

  const isFormulaComplete = (s: string) => {
    const raw = String(s ?? "").trim();
    if (!raw.startsWith("=")) return false;
    const afterEq = raw.slice(1);
    // Consider a formula complete when parentheses are balanced
    // and it looks like a function call or any expression that can be evaluated
    const looksCallable = /^[A-Za-z]+\s*\(.*\)$/.test(afterEq) || /[+\-*/^]/.test(afterEq) || /[A-Za-z]+\d/.test(afterEq);
    return looksCallable && hasBalancedParens(afterEq);
  };

  // REPLACE handleFormulaSubmit to accept optional navigation and start editing next cell
  const handleFormulaSubmit = (navigate?: 'down' | 'up' | 'right' | 'left') => {
    const raw = String(formulaInput ?? "").trim();
    const commitValue = raw.startsWith("=") ? raw : (isFormulaLike(raw) ? "=" + raw : raw);

    // Commit the value/formula to the data model
    handleCellChange(selectedCell, commitValue);
    setIsSaved(false);
    setSuggestionsOpen(false);

    // Blur whichever editor was active to ensure the value renders
    if (activeEditor === 'formulaBar') {
      formulaInputRef.current?.blur();
    } else if (activeEditor === 'cell') {
      cellEditorRef.current?.blur();
    } else {
      formulaInputRef.current?.blur();
      cellEditorRef.current?.blur();
    }

    // Excel-style navigation if requested; otherwise just exit editing
    if (navigate) {
      navigateAfterSubmit(navigate);
    } else {
      setActiveEditor(null);
    }
  };

  // NEW: Save / Save As like MapleWord, using .msheet JSON and Supabase
  const buildContentJSON = () => {
    return JSON.stringify(
      {
        name: sheetName,
        data, // cells + formulas
        charts: chartOverlays, // draggable chart images and positions
        version: 1,
        updatedAt: new Date().toISOString(),
      },
      null,
      2
    );
  };

  // Helper: resolve Documents path for current user (same pattern as SaveAsDialog)
  const resolveDocumentsPath = async (): Promise<string> => {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    let username = "User";
    if (user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("username,email")
        .eq("id", user.id)
        .maybeSingle();
      const candidate =
        profile?.username ||
        (profile?.email || user.email || "User").split("@")[0] ||
        "User";
      username = (candidate.replace(/[^a-zA-Z0-9]/g, "") || "User");
    }
    return `C:\\User\\${username}\\Documents`;
  };

  const sanitizeFileName = (name: string) =>
    String(name ?? "")
      .replace(/[<>:"/\\|?*]+/g, "")
      .trim();

  // NEW: Save should auto-target Documents\\MapleSheet when no currentFilePath exists
  const handleSave = async () => {
    if (currentFilePath) {
      const parent = currentFilePath.substring(0, currentFilePath.lastIndexOf("\\"));
      await saveToVFSAndSupabase(currentFilePath, parent);
    } else {
      const docs = await resolveDocumentsPath();
      const targetDir = `${docs}\\MapleSheet`;
      await ensureDir(targetDir);

      // Build file name with .msheet extension
      const base = sanitizeFileName(sheetName || "Untitled Spreadsheet");
      const stripped = base.replace(/\.(json|csv|msheet)$/i, "");
      const fileName = `${stripped}.msheet`;
      const filePath = `${targetDir}\\${fileName}`;

      await saveToVFSAndSupabase(filePath, targetDir);
    }
  };

  const handleSaveAs = () => {
    const base = sheetName.trim();
    const stripped = base.replace(/\.(json|csv|msheet)$/i, '');
    const next = stripped.toLowerCase().endsWith('.msheet') ? stripped : `${stripped}.msheet`;
    setSaveFileName(next || 'untitled.msheet');
    setShowSaveDialog(true);
  };

  const handleSaveLocation = async (selectedFolder: any) => {
    const rawName = saveFileName || `${sheetName}.msheet`;
    const fileName = rawName.toLowerCase().endsWith('.msheet')
      ? rawName
      : `${rawName.replace(/\.(json|csv)$/i, '')}.msheet`;
    const base = (selectedFolder?.path || selectedFolder?.file_path || 'C:\\').trim();
    const basePath = base.endsWith('\\') ? base : `${base}\\`;
    const filePath = `${basePath}${fileName}`;
    await saveToVFSAndSupabase(filePath, basePath.slice(0, -1));
    setShowSaveDialog(false);
  };

  const saveToVFSAndSupabase = async (path: string, parentPath?: string, opts?: { silent?: boolean }) => {
    if (!opts?.silent) setIsSaving(true);
    try {
      const content = buildContentJSON();
      const dir = parentPath || path.substring(0, path.lastIndexOf('\\'));
      await ensureDir(dir);
      await globalVFS.save(path, content);
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: dir } }));
      if (user) {
        await saveToSupabase(path, dir, content, 'application/x-maplesheet');
      }
      setCurrentFilePath(path);
      setIsSaved(true);
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: 'Failed to save the spreadsheet. Please try again.',
        variant: 'destructive',
      });
    } finally {
      if (!opts?.silent) setIsSaving(false);
    }
  };

  const saveToSupabase = async (path: string, parentPath: string | undefined, content: string, contentType?: string) => {
    if (!user) return;
    try {
      const fileName = path.split('\\').pop() || 'Untitled.msheet';
      const parent = parentPath || path.substring(0, path.lastIndexOf('\\'));
      const { data: existingFile } = await supabase
        .from('os_user_files')
        .select('id')
        .eq('user_id', user.id)
        .eq('file_path', path)
        .maybeSingle();
      let result;
      if (existingFile) {
        result = await supabase
          .from('os_user_files')
          .update({
            content,
            size_bytes: content.length,
            updated_at: new Date().toISOString(),
            ...(contentType ? { content_type: contentType } : {}),
          })
          .eq('id', existingFile.id)
          .eq('user_id', user.id);
      } else {
        result = await supabase
          .from('os_user_files')
          .insert({
            user_id: user.id,
            file_path: path,
            file_name: fileName,
            parent_path: parent,
            content,
            content_type: contentType || 'application/json',
            size_bytes: content.length,
            is_directory: false,
          });
      }
      if ((result as any).error) throw (result as any).error;
    } catch (error) {
      console.error('[MapleSheet] saveToSupabase error:', error);
      toast({ title: 'Save Failed', description: 'Could not save to cloud.', variant: 'destructive' });
    }
  };

  // NEW: Open via file dialog
  const handleOpen = () => {
    setShowFileBrowser(true);
  };

  const handleFileSelect = async (file: any) => {
    if (!file?.content) {
      toast({ title: 'Error', description: 'Selected file has no content.', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const parsed = JSON.parse(file.content);
      setSheetName(parsed?.name || 'Untitled Spreadsheet');
      setData(parsed?.data || {});
      setChartOverlays(parsed?.charts || []);
      setCurrentFilePath(file.file_path || file.path || '');
      toast({ title: 'Spreadsheet Loaded', description: `Loaded: ${file.file_name || file.name}` });
    } catch (e) {
      toast({ title: 'Load Failed', description: 'File is not a valid MapleSheet document.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Autosave while editing when a path exists
  useEffect(() => {
    if (!currentFilePath) return;
    const parent = currentFilePath.substring(0, currentFilePath.lastIndexOf('\\'));
    const timer = setTimeout(async () => {
      setAutoSaving(true);
      await saveToVFSAndSupabase(currentFilePath, parent, { silent: true });
      setAutoSaving(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [data, chartOverlays, sheetName, currentFilePath]);

  // NEW: Initial autosave for untitled sheets
  useEffect(() => {
    if (currentFilePath) return;
    const hasAnyData = Object.keys(data).length > 0 || chartOverlays.length > 0;
    if (!hasAnyData || !usernameReady) return;
    const timer = setTimeout(async () => {
      const docs = getUserDocumentsPath();
      const draftsPath = `${docs}\\MapleSheet\\Drafts`;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filePath = `${draftsPath}\\Untitled-${timestamp}.msheet`;
      await saveToVFSAndSupabase(filePath, draftsPath, { silent: true });
    }, 1000);
    return () => clearTimeout(timer);
  }, [data, chartOverlays, currentFilePath, usernameReady, osUsername]);

  const handleNewSheet = () => {
    setData({} as SpreadsheetData);
    setSheetName('Untitled Spreadsheet');
    setSelectedCell('A1');
    setFormulaInput('');
    setChartOverlays([]);
    setIsSaved(false);
    
    toast({
      title: "New Spreadsheet",
      description: "Started a new spreadsheet.",
    });
  };

  const insertRow = () => {
    // This would insert a new row in a more complete implementation
    toast({
      title: "Feature Coming Soon",
      description: "Row insertion will be available in the next update.",
    });
  };

  const insertColumn = () => {
    // This would insert a new column in a more complete implementation
    toast({
      title: "Feature Coming Soon",
      description: "Column insertion will be available in the next update.",
    });
  };

  // Determine last used row so exports/charts only process active data
  const getMaxRowUsed = () => {
    let max = 1;
    for (const id in data) {
      const cell = data[id];
      if (!cell) continue;
      const raw = (cell.formula ?? cell.value ?? '').trim();
      if (!raw) continue;
      const m = id.match(/^[A-Za-z]+(\d+)$/);
      if (m) {
        const r = Math.max(1, parseInt(m[1], 10));
        if (r > max) max = r;
      }
    }
    return max;
  };

  const buildChartTable = () => {
    // Headers from first row; fallback to column letters
    const headers: string[] = Array.from({ length: COLS }, (_, col) => {
      const h = getCellValue(getCellId(0, col)).trim();
      return h || COLUMN_LETTERS[col];
    });
    const rows: Record<string, string | number>[] = [];
    const maxRow = Math.min(getMaxRowUsed(), 10000); // cap for performance in charts
    for (let row = 1; row < maxRow; row++) {
      const obj: Record<string, string | number> = {};
      let hasValue = false;
      for (let col = 0; col < COLS; col++) {
        const cellId = getCellId(row, col);
        const raw = getCellValue(cellId).trim();
        if (raw !== "") {
          const num = Number(raw);
          obj[headers[col]] = !isNaN(num) ? num : raw;
          hasValue = true;
        }
      }
      if (hasValue) rows.push(obj);
    }
    return { headers, rows };
  };

  // Export the entire sheet as CSV with evaluated formulas
  const handleExportCSV = () => {
    const csvEscape = (val: string) => {
      const text = val ?? "";
      return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    try {
      const lines: string[] = [];
      const maxRow = getMaxRowUsed(); // export up to last used row
      for (let row = 0; row < maxRow; row++) {
        const cols: string[] = [];
        for (let col = 0; col < COLS; col++) {
          const cellId = getCellId(row, col);
          const value = getCellValue(cellId);
          cols.push(csvEscape(value));
        }
        lines.push(cols.join(","));
      }
      const csv = lines.join("\r\n");
      const base = sheetName.trim() || "Untitled Spreadsheet";
      const fileName = /\.csv$/i.test(base) ? base : `${base}.csv`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      saveAs(blob, fileName);
      toast({ title: "CSV Exported", description: `Downloaded ${fileName}` });
    } catch {
      toast({ title: "Export Failed", description: "Could not export CSV.", variant: "destructive" });
    }
  };

  const handleCreateChart = (payload: { imageSrc: string; config: ChartOverlayConfig }) => {
    const id = `chart_${Date.now()}`;
    const offsetX = 120;
    const offsetY = 80;
    setChartOverlays((prev) => [
      ...prev,
      { id, src: payload.imageSrc, config: payload.config, x: offsetX, y: offsetY, width: 360 },
    ]);
    toast({
      title: "Chart inserted",
      description: "A chart has been added to your sheet. Drag to move and use corners to resize.",
    });
  };

  const moveChart = (id: string, x: number, y: number) => {
    setChartOverlays((prev) => prev.map((c) => (c.id === id ? { ...c, x, y } : c)));
  };

  const resizeChart = (id: string, x: number, y: number, width: number) => {
    setChartOverlays((prev) => prev.map((c) => (c.id === id ? { ...c, x, y, width } : c)));
  };

  const updateSuggestions = (value: string) => {
    const val = String(value ?? '');
    if (!val.startsWith('=')) {
      setSuggestionsOpen(false);
      setSuggestions([]);
      setHighlightIndex(0);
      return;
    }
    const afterEq = val.slice(1);
    // If the user already typed '(', hide suggestions so Enter won't insert a function
    if (afterEq.includes('(')) {
      setSuggestionsOpen(false);
      return;
    }
    const match = afterEq.match(/^[A-Za-z]+/);
    const token = (match ? match[0] : '').toUpperCase();
    const filtered = EXCEL_FUNCTIONS
      .filter(fn => token.length === 0 || fn.name.startsWith(token))
      .slice(0, 20);
    setSuggestions(filtered);
    setSuggestionsOpen(filtered.length > 0);
    setHighlightIndex(0);
  };

  // In suggestions, insert function and focus whichever editor is active
  const insertFunction = (fnName: string) => {
    const current = formulaInput;
    const afterEq = current.startsWith('=') ? current.slice(1) : '';
    const match = afterEq.match(/^([A-Za-z]*)(.*)$/);
    const rest = (match ? match[2] : '');
    const hasParen = /\(/.test(rest);
    const newValue = '=' + fnName + (hasParen ? rest : '()' + rest);
    setFormulaInput(newValue);
    setSuggestionsOpen(false);
    // place caret inside parentheses
    const caretPos = ('=' + fnName + '(').length;
    setTimeout(() => {
      const el = activeEditor === 'formulaBar' ? formulaInputRef.current : cellEditorRef.current;
      el?.focus();
      el?.setSelectionRange(caretPos, caretPos);
    }, 0);
  };

  // ADD: Wrapper so inserts from the library focus the formula bar and then insert
  const handleInsertFromLibrary = (name: string) => {
    if (activeEditor !== 'formulaBar') {
      setActiveEditor('formulaBar');
      setTimeout(() => {
        formulaInputRef.current?.focus();
        // Ensure we are in formula mode if empty
        if (!String(formulaInput ?? '').startsWith('=')) {
          // Preload '=' so insertFunction composes correctly
          setFormulaInput('=');
          setTimeout(() => insertFunction(name), 0);
        } else {
          insertFunction(name);
        }
      }, 0);
    } else {
      insertFunction(name);
    }
  };

  return (
    <div className="relative flex flex-col h-full bg-background">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Input
              value={sheetName}
              onChange={(e) => { setSheetName(e.target.value); setIsSaved(false); }}
              className="max-w-64"
              placeholder="Spreadsheet name"
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  await handleSave();
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Selected: {selectedCell}</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={handleNewSheet} size="sm" variant="outline">
            <Sheet className="w-4 h-4 mr-1" />
            New
          </Button>
          <Button size="sm" variant="outline" disabled={isLoading} onClick={handleOpen}>
            <FolderOpen className="w-4 h-4 mr-1" />
            Open
          </Button>
          <Button onClick={handleSave} size="sm" variant="outline" disabled={isSaving}>
            <Save className="w-4 h-4 mr-1" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          
          <div className="h-6 w-px bg-border mx-2" />
          
          <Button onClick={insertRow} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Insert Row
          </Button>
          <Button onClick={insertColumn} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Insert Column
          </Button>
          
          <div className="h-6 w-px bg-border mx-2" />
          
          <Button onClick={handleExportCSV} size="sm" variant="outline">
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const table = buildChartTable();
              setChartTable(table);
              setChartsOpen(true);
              toast({
                title: "Charts",
                description: "Configure a chart from your sheet data.",
              });
            }}
           >
             <BarChart3 className="w-4 h-4 mr-1" />
             Charts
           </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setFormulasOpen(true)}
          >
             <Calculator className="w-4 h-4 mr-1" />
             Functions
           </Button>
        </div>

        {/* Formula Bar */}
        <div className="mt-4">
          <div className="relative flex items-center gap-2">
            <span className="text-sm font-medium min-w-12">{selectedCell}</span>
            <Input
              ref={formulaInputRef}
              onFocus={() => setActiveEditor('formulaBar')}
              value={formulaInput}
              onChange={(e) => {
                setFormulaInput(e.target.value);
                updateSuggestions(e.target.value);
                updateCaretFromInput();
              }}
              onClick={updateCaretFromInput}
              onKeyUp={updateCaretFromInput}
              onSelect={updateCaretFromInput}
              onBlur={() => setActiveEditor(null)}
              onKeyDown={(e) => {
                // NEW: while editing a formula, Shift + Arrow extends range selection instead of committing
                if (
                  isFormulaMode() &&
                  e.shiftKey &&
                  ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)
                ) {
                  e.preventDefault();
                  startKeyboardRangeSelection();
                  moveSelectionEndByArrow(e.key as 'ArrowDown' | 'ArrowUp' | 'ArrowLeft' | 'ArrowRight');
                  return;
                }

                if (suggestionsOpen) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlightIndex((i) => Math.min(i + 1, (suggestions.length || 1) - 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlightIndex((i) => Math.max(i - 1, 0));
                  } else if (e.key === 'Enter') {
                    // If the formula is complete, submit; otherwise insert selected function
                    if (isFormulaComplete(formulaInput) || suggestions.length === 0) {
                      e.preventDefault();
                      handleFormulaSubmit(e.shiftKey ? 'up' : 'down');
                      return;
                    } else {
                      e.preventDefault();
                      insertFunction(suggestions[highlightIndex]?.name || suggestions[0].name);
                      return;
                    }
                  } else if (e.key === 'Escape') {
                    setSuggestionsOpen(false);
                  } else if (e.key === 'Tab') {
                    e.preventDefault();
                    handleFormulaSubmit(e.shiftKey ? 'left' : 'right');
                  }
                } else {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFormulaSubmit(e.shiftKey ? 'up' : 'down');
                  } else if (e.key === 'Tab') {
                    e.preventDefault();
                    handleFormulaSubmit(e.shiftKey ? 'left' : 'right');
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    handleFormulaSubmit('down');
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    handleFormulaSubmit('up');
                  } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    handleFormulaSubmit('right');
                  } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    handleFormulaSubmit('left');
                  }
                }
              }}
              placeholder="Enter value or formula (e.g., =A1+B1, =SUM(A1:A10))"
              className="flex-1"
            />
            <Button onClick={handleFormulaSubmit} size="sm">
              ✓
            </Button>

            {suggestionsOpen && suggestions.length > 0 && (
              <FormulaSuggestions
                items={suggestions}
                highlightIndex={highlightIndex}
                onSelect={(item) => insertFunction(item.name)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Content area: single scroller + pinned status bar */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="flex-1 p-4 overflow-auto" ref={containerRef}>
          <Card
            className={sheetCardClassName}
            style={lightSheetCardStyle}
            onMouseUp={() => {
              // Complete range selection on mouse up anywhere over the sheet
              finalizeRangeSelection();
            }}
          >
            <div className="min-w-full">
              <table className="maplesheet-grid-table w-full border-collapse" style={lightSheetTableStyle}>
                <thead className="sticky top-0 z-30">
                  <tr>
                    {/* Top-left corner: freeze both top and left, sit above row index */}
                    <th
                      className={sheetCornerCellClassName}
                      style={lightSheetHeaderStyle}
                    ></th>
                    {Array.from({ length: COLS }, (_, col) => (
                      <th
                        key={col}
                        className={sheetHeaderCellClassName}
                        style={lightSheetHeaderStyle}
                      >
                        {COLUMN_LETTERS[col]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Top spacer to preserve scroll height */}
                  {topPad > 0 && (
                    <tr aria-hidden>
                      <td colSpan={COLS + 1} style={{ height: topPad }} />
                    </tr>
                  )}
                  {Array.from({ length: Math.max(0, visibleEndRow - visibleStartRow) }, (_, i) => {
                    const row = visibleStartRow + i;
                    return (
                    <tr key={row}>
                      {/* Frozen left row index column (under header) */}
                      <td className={sheetRowHeaderClassName} style={lightSheetRowHeaderStyle}>
                        {row + 1}
                      </td>
                      {Array.from({ length: COLS }, (_, col) => {
                        const cellId = getCellId(row, col);
                        const isSelected = selectedCell === cellId;
                        const inDragSelect = isCellInSelection(row, col);

                        // Show raw formula/text while actively editing the selected cell; otherwise show evaluated value
                        const getDisplayValue = () => {
                          // While editing (either in the cell or in the formula bar), mirror the live buffer
                          if (isSelected && (activeEditor === 'cell' || activeEditor === 'formulaBar')) {
                            return formulaInput;
                          }
                          return getCellValue(cellId);
                        };

                        return (
                          <td
                            key={col}
                            className={getGridCellClassName(isSelected, inDragSelect)}
                            style={getLightGridCellStyle(isSelected, inDragSelect)}
                            onMouseDown={(e) => {
                              if (isFormulaMode()) {
                                e.preventDefault();
                                e.stopPropagation();
                                beginRangeSelection(row, col);
                              } else {
                                handleCellSelect(cellId);
                              }
                            }}
                            onMouseEnter={() => {
                              if (isSelectingRange) {
                                updateRangeSelection(row, col);
                              }
                            }}
                            onClick={(e) => {
                              if (!isFormulaMode()) {
                                handleCellSelect(cellId);
                                return;
                              }
                              if (e.shiftKey) {
                                updateRangeSelection(row, col);
                                finalizeRangeSelection();
                                return;
                              }
                              beginRangeSelection(row, col);
                            }}
                          >
                            <input
                              type="text"
                              ref={(el) => { if (isSelected) cellEditorRef.current = el as HTMLInputElement; }}
                              value={getDisplayValue()}
                              onFocus={() => {
                                setActiveEditor('cell');
                                handleCellSelect(cellId);
                              }}
                              onChange={(e) => {
                                handleCellChange(cellId, e.target.value);
                                setFormulaInput(e.target.value);
                                updateSuggestions(e.target.value);
                              }}
                              onClick={updateCaretFromInput}
                              onKeyUp={updateCaretFromInput}
                              onSelect={updateCaretFromInput}
                              onBlur={() => setActiveEditor(null)}
                              onKeyDown={(e) => {
                                // NEW: while editing a formula, Shift + Arrow extends range selection instead of committing
                                if (
                                  isFormulaMode() &&
                                  e.shiftKey &&
                                  ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)
                                ) {
                                  e.preventDefault();
                                  startKeyboardRangeSelection();
                                  moveSelectionEndByArrow(e.key as 'ArrowDown' | 'ArrowUp' | 'ArrowLeft' | 'ArrowRight');
                                  return;
                                }

                                // Mirror suggestions behavior while editing in-cell
                                if (suggestionsOpen) {
                                  if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setHighlightIndex((i) => Math.min(i + 1, (suggestions.length || 1) - 1));
                                  } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setHighlightIndex((i) => Math.max(i - 1, 0));
                                  } else if (e.key === 'Enter') {
                                    // If the formula is complete, submit; otherwise insert selected function
                                    if (isFormulaComplete(formulaInput) || suggestions.length === 0) {
                                      e.preventDefault();
                                      handleFormulaSubmit(e.shiftKey ? 'up' : 'down');
                                      return;
                                    } else {
                                      e.preventDefault();
                                      insertFunction(suggestions[highlightIndex]?.name || suggestions[0].name);
                                      return;
                                    }
                                  } else if (e.key === 'Escape') {
                                    setSuggestionsOpen(false);
                                  } else if (e.key === 'Tab') {
                                    e.preventDefault();
                                    handleFormulaSubmit(e.shiftKey ? 'left' : 'right');
                                  }
                                } else {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleFormulaSubmit(e.shiftKey ? 'up' : 'down');
                                  } else if (e.key === 'Tab') {
                                    e.preventDefault();
                                    handleFormulaSubmit(e.shiftKey ? 'left' : 'right');
                                  } else if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    handleFormulaSubmit('down');
                                  } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    handleFormulaSubmit('up');
                                  } else if (e.key === 'ArrowRight') {
                                    e.preventDefault();
                                    handleFormulaSubmit('right');
                                  } else if (e.key === 'ArrowLeft') {
                                    e.preventDefault();
                                    handleFormulaSubmit('left');
                                  }
                                }
                              }}
                              className={`maplesheet-grid-input w-full h-full px-1 text-xs bg-transparent border-none outline-none text-foreground ${
                                isSelected ? 'caret-primary' : ''
                              }`}
                              readOnly={isSelectingRange} // prevent input focus while selecting a range
                            />
                          </td>
                        );
                      })}
                    </tr>
                  )})}
                  {/* Bottom spacer to preserve scroll height */}
                  {bottomPad > 0 && (
                    <tr aria-hidden>
                      <td colSpan={COLS + 1} style={{ height: bottomPad }} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Floating chart overlays */}
            {chartOverlays.map((item) => (
              <ChartOverlay
                key={item.id}
                item={item}
                table={buildChartTable()}
                onMove={moveChart}
                onResize={resizeChart}
              />
            ))}
          </Card>
        </div>
        {/* Pinned Status Bar */}
        <MapleSheetStatusBar
          selectedCell={selectedCell}
          sheetName={sheetName}
          currentFilePath={currentFilePath}
          nonEmptyCells={nonEmptyCells}
          isSaving={isSaving}
          autoSaving={autoSaving}
          isSaved={isSaved}
          onSave={handleSave}
        />
      </div>

      {/* AI Toolbar overlay */}
      <MapleSheetAiToolbar
        selectedCell={selectedCell}
        getCellValue={getCellValue}
        setCellValue={(id, value) => handleCellChange(id, value)}
        applyTableAt={applyTableAt}
        openChartsConfigurator={() => {
          const table = buildChartTable();
          setChartTable(table);
          setChartsOpen(true);
          toast({ title: 'Charts', description: 'Configure a chart from your sheet data.' });
        }}
      />

      {/* Portaled/overlay dialogs (outside scroller) */}
      {/* Charts Modal */}
      <MapleSheetChartModal
        open={chartsOpen}
        onOpenChange={setChartsOpen}
        table={chartTable}
        sheetName={sheetName}
        onCreateChart={handleCreateChart}
      />
      {/* Function Library */}
      <FormulaLibraryDialog
        open={formulasOpen}
        onOpenChange={setFormulasOpen}
        onInsert={handleInsertFromLibrary}
      />
      {/* File dialogs */}
      <DraggableFileDialog
        isOpen={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        onFileSelect={handleFileSelect}
        mode="open"
        acceptedTypes={['application/x-maplesheet', 'application/json']}
        title="Open Spreadsheet"
      />
      <DraggableFileDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        mode="save"
        initialPath="C:\\"
        title="Save Spreadsheet"
        fileName={saveFileName}
        onFileNameChange={setSaveFileName}
        onSave={handleSaveLocation}
      />
    </div>
  );
};

const TOTAL_ROWS = 100000; // large logical row count
const ROW_HEIGHT = 32; // px, matching h-8
const VIRTUAL_BUFFER = 30; // extra rows to render above/below viewport
const COLS = 10;
const COLUMN_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const getCellId = (row: number, col: number): string => {
  return `${COLUMN_LETTERS[col]}${row + 1}`;
};